"use client"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useParams } from "next/navigation"
import { jsonLdScript } from "@/lib/jsonld"
import { sellerDisplayName } from "@/lib/accounts/display-name"

/** The raw enum ("OWNER") was being printed straight onto the page. */
const ROLE_LABEL: Record<string, string> = {
  AGENT: "Estate Agent",
  OWNER: "Property Owner",
  DEVELOPER: "Property Developer",
}

export default function AgentProfilePage() {
  const params = useParams()
  const [agent, setAgent] = useState<any>(null)
  const [listings, setListings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch("/api/agents/" + params.id).then(r => r.json()),
      fetch("/api/listings?agentId=" + params.id + "&status=LIVE").then(r => r.json()),
    ]).then(([agentData, listingsData]) => {
      if (agentData.success) setAgent(agentData.data)
      if (listingsData.success) setListings(listingsData.data ?? [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [params.id])

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)" }}>
      <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, color: "var(--navy)", opacity: 0.5 }}>Loading...</div>
    </div>
  )

  if (!agent) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)" }}>
      <div style={{ textAlign: "center" }}>
        <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, color: "var(--navy)", marginBottom: 16 }}>Seller not found</h1>
        <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 20 }}>This seller has no live listings, or the link is out of date.</p>
        <Link href="/listings" style={{ color: "var(--gold-deep)", fontWeight: 600 }}>Browse all properties</Link>
      </div>
    </div>
  )

  const profile = agent.agent
  const initials = agent.name ? agent.name.split(" ").map((n: string) => n[0]).join("").substring(0, 2).toUpperCase() : "ZE"
  // Server-side redirect rather than a wa.me built here: the profile API still
  // returns the number (this page is the one place a buyer legitimately looks a
  // seller up), but keeping it out of the rendered href means it is not sitting
  // in the HTML of a public, crawlable, CDN-cached page for a scraper to lift.
  const waLink = agent.hasPhone ? "/api/go/whatsapp?agent=" + encodeURIComponent(String(params.id)) : null
  // tel: needs the literal number, which the API returns only to signed-in callers.
  const telLink = agent.phone ? "tel:" + agent.phone : null
  const isVerified = agent.kycSubmission?.status === "APPROVED"

  const SITE = "https://zimestates.co.zw"
  const agentLd: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: agent.name,
    url: SITE + "/agents/" + params.id,
    ...(profile?.photoUrl ? { image: profile.photoUrl } : {}),
    ...(agent.email       ? { email: agent.email }       : {}),
    ...(agent.phone       ? { telephone: agent.phone }   : {}),
    ...(profile?.bio      ? { description: profile.bio } : {}),
    ...(profile?.agency?.name ? { memberOf: { "@type": "Organization", name: profile.agency.name } } : {}),
    address: { "@type": "PostalAddress", addressCountry: "ZW" },
    ...(isVerified ? { hasCredential: { "@type": "EducationalOccupationalCredential", credentialCategory: "KYC Verified", recognizedBy: { "@type": "Organization", name: "Zim Estates" } } } : {}),
    ...(listings.length > 0 ? { numberOfItems: listings.length } : {}),
  }
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",   item: SITE },
      { "@type": "ListItem", position: 2, name: "Agents", item: SITE + "/agents" },
      { "@type": "ListItem", position: 3, name: agent.name, item: SITE + "/agents/" + params.id },
    ],
  }

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(agentLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(breadcrumbLd) }} />
      <div style={{ background: "var(--navy)", padding: "48px 24px 0" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Link href="/agents" style={{ color: "rgba(253,251,247,0.5)", fontSize: 13, textDecoration: "none", display: "inline-block", marginBottom: 24 }}>{"<"} All agents</Link>
          <div style={{ display: "flex", gap: 28, alignItems: "flex-end", paddingBottom: 40 }}>
            <div style={{ width: 96, height: 96, borderRadius: "50%", background: profile?.photoUrl ? `url(${profile.photoUrl}) center/cover` : "var(--gold)", display: "grid", placeItems: "center", fontSize: 32, fontWeight: 700, color: "var(--navy)", flexShrink: 0, overflow: "hidden", border: "3px solid rgba(253,251,247,0.15)" }}>
              {!profile?.photoUrl && initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 700, color: "var(--cream)", margin: 0 }}>{sellerDisplayName({ ...agent, agent: profile ? { agency: profile.agency } : null })}</h1>
                {isVerified && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", background: "rgba(200,150,62,0.15)", padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap" }}>KYC Verified</span>
                )}
                {profile?.eacLicense && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(253,251,247,0.7)", background: "rgba(255,255,255,0.08)", padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap" }}>EAC {profile.eacLicense}</span>
                )}
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "rgba(253,251,247,0.6)" }}>{ROLE_LABEL[agent.role as string] ?? "Verified seller"}</span>
                {profile?.agency?.name && (
                  <Link href={"/agents?agency=" + encodeURIComponent(profile.agency.name)} style={{ fontSize: 13, color: "rgba(253,251,247,0.6)", textDecoration: "none", borderBottom: "1px solid rgba(253,251,247,0.2)" }}>{profile.agency.name}</Link>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
              {waLink && (
                <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ padding: "10px 20px", background: "#075E54", color: "white", borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>WhatsApp</a>
              )}
              {telLink && (
                <a href={telLink} style={{ padding: "10px 20px", border: "1px solid rgba(253,251,247,0.25)", color: "var(--cream)", borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>Call</a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 24px", display: "flex", gap: 40, alignItems: "flex-start" }} className="mob-stack">
        <div style={{ width: 280, flexShrink: 0 }}>
          {(profile?.responseRate > 0 || profile?.totalSales > 0 || listings.length > 0) && (
            <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, padding: 24, marginBottom: 20 }}>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 600, color: "var(--navy)", marginBottom: 16 }}>Stats</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 700, color: "var(--navy)" }}>{listings.length}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>Active listings</div>
                </div>
                {profile?.totalSales > 0 && (
                  <div>
                    <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 700, color: "var(--green-text)" }}>{profile.totalSales}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>Sales closed</div>
                  </div>
                )}
                {profile?.responseRate > 0 && (
                  <div>
                    <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 700, color: "var(--gold-deep)" }}>{Math.round(profile.responseRate)}%</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>Response rate</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {(profile?.specialisations?.length > 0 || profile?.languages?.length > 0) && (
            <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, padding: 24, marginBottom: 20 }}>
              {profile?.specialisations?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 10 }}>Specialises in</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {profile.specialisations.map((s: string) => (
                      <span key={s} style={{ fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 20, background: "rgba(27,45,84,0.06)", color: "var(--navy)" }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {profile?.languages?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 10 }}>Languages</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {profile.languages.map((l: string) => (
                      <span key={l} style={{ fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 20, background: "rgba(61,139,95,0.07)", color: "var(--green-text)" }}>{l}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, padding: 24 }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 600, color: "var(--navy)", marginBottom: 12 }}>Contact</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {waLink && (
                <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ display: "block", padding: "12px 16px", background: "#075E54", color: "white", borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: "none", textAlign: "center" }}>WhatsApp {sellerDisplayName({ ...agent, agent: profile ? { agency: profile.agency } : null }).split(" ")[0]}</a>
              )}
              {agent.email && (
                <a href={"mailto:" + agent.email} style={{ display: "block", padding: "12px 16px", background: "var(--cream-warm)", color: "var(--navy)", border: "1px solid var(--line)", borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: "none", textAlign: "center" }}>
                  Email {agent.name?.split(" ")[0]}
                </a>
              )}
              {!waLink && !agent.email && (
                <div style={{ fontSize: 13, color: "var(--muted)" }}>No contact info available</div>
              )}
              {agent.calcomLink && (
                <a href={agent.calcomLink} target="_blank" rel="noopener noreferrer" style={{ display: "block", padding: "12px 16px", background: "var(--gold)", color: "var(--navy)", borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: "none", textAlign: "center" }}>Book a viewing</a>
              )}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {profile?.bio && (
            <div style={{ marginBottom: 36 }}>
              <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)", marginBottom: 14 }}>About</h2>
              <p style={{ color: "var(--ink)", fontSize: 15, lineHeight: 1.7, margin: 0 }}>{profile.bio}</p>
            </div>
          )}

          <div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)", marginBottom: 20 }}>
              Active listings <span style={{ color: "var(--muted)", fontSize: 15, fontWeight: 400 }}>({listings.length})</span>
            </h2>
            {listings.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: "var(--muted)", background: "var(--cream-warm)", borderRadius: 14, border: "1px solid var(--line)" }}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, color: "var(--navy)", marginBottom: 8 }}>No active listings</div>
                <div style={{ fontSize: 13 }}>This agent has no active listings at the moment.</div>
              </div>
            ) : (
              <div className="listings-grid">
                {listings.map(l => {
                  const photo = l.photos?.[0]?.url
                  return (
                    <Link key={l.id} href={"/listings/" + l.id} className="listing-card">
                      <div className="listing-img" style={{ background: photo ? undefined : "linear-gradient(135deg,#3a4f7a,#1B2D54)" }}>
                        {photo && <Image src={photo} alt={l.title} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />}
                        <span className="listing-tag">{l.priceType === "SALE" ? "FOR SALE" : "FOR RENT"}</span>
                        {l.isFeatured && <span className="listing-tag" style={{ background: "var(--gold)", color: "var(--navy)", left: "auto", right: 10 }}>FEATURED</span>}
                      </div>
                      <div className="listing-body">
                        <div className="listing-price">${l.price?.toLocaleString()}</div>
                        <div className="listing-title">{l.title}</div>
                        <div className="listing-loc">{l.suburb}, {l.city}</div>
                        {(l.beds || l.baths) && (
                          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                            {l.beds ? l.beds + " bed" : ""}{l.beds && l.baths ? " · " : ""}{l.baths ? l.baths + " bath" : ""}
                          </div>
                        )}
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
